import { useEffect, useState } from "react";
import useApiCallService from "../../Common/Helpers/ApiCall";
import SortableList from "../../Common/Components/Lists/SortableList";
import { Button, Col } from "reactstrap";
import { ADMIN_PATHS } from "../../config";
import CostBearerModel from "../Models/Response/CostBearerModel";
import { useTranslation } from "react-i18next";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { SettingsListHeader } from "../../SystemSettings/Components/SettingsListHeader";
import { CostBearerCreatePanel } from "../Components/CostBearerCreatePanel";
import { CostBearerUpdatePanel } from "../Components/CostBearerUpdatePanel";

const AdminCostBearerList = () => {
    const isSuperAdmin = CheckUserRole("SuperAdmin");
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [displayReactivateModal, setDisplayReactivateModal] = useState(false);
    const [deleteApiResponse, setDeleteApiResponse] = useState(null);
    const [costBearerId, setCostBearerId] = useState(null);
    const [createPanelOpen, setCreatePanelOpen] = useState(false);
    const [editCostBearerId, setEditCostBearerId] = useState(null);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('costBearer.costBearer'));
        setPageTitleKey('costBearer.costBearer');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    let permissionUpdate = false;
    if (CheckUserPermission("system.setting.cost-bearer.update") || CheckUserPermission("system.setting.cost-bearer") || CheckUserPermission("system") || CheckUserPermission("system.setting")) {
        permissionUpdate = true;
    }

    const renderActionColumn = (item) => {
        return (
            <>
                {permissionUpdate === true &&
                    <Button 
                        className="box-red border-0 position-relative me-2 rounded" 
                        onClick={() => {
                            setDisplayDeleteModal(true);
                            setCostBearerId(item.id);
                        }}
                    >
                        <i className="bx bx-x-circle fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">
                            {t("button.delete")}
                        </div>
                    </Button>
                }
                {permissionUpdate === true &&
                    <Button 
                        className="bg-green-light text-green border-0 position-relative" 
                        onClick={() => setEditCostBearerId(item.id)}
                    >
                        <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">
                            {t("button.edit")}
                        </div>
                    </Button>
                }
            </>
        );
    }

    // HANDLE MODAL DELETE
    const handleModalDelete = async () => {
        const apiCallOptions = {
            method: "delete",
            url: `/costbearer?id=${costBearerId}`
        };
        try {
            await ApiCall(apiCallOptions, setDeleteApiResponse);
            setDisplayDeleteModal(false);
        } catch (error) {
            console.log(error);
        }
    }

    // HANDLE MODAL REACTIVATE
    const handleModalReactivate = async () => {
        try {
            const apiCallOptions = {
                method: "put",
                url: `/costbearer/reactivate?id=${costBearerId}`,
            };
            await ApiCall(apiCallOptions, (apiResponse) => {
                if (apiResponse) {
                    FlashMessage(t('validationMessage.costBearerReactivated'), 'flash-messageSuccess');
                    setDisplayReactivateModal(false);
                    setTimeout(() => {
                        CustomReload(ADMIN_PATHS.ADMIN_COST_BEARER);
                    }, 1500);
                }
            });
        } catch (error) {
            console.log(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    }

    useEffect(() => {
        if (deleteApiResponse !== null) {
            CustomReload(ADMIN_PATHS.ADMIN_COST_BEARER);
        }
    }, [deleteApiResponse]);

    const modalContentDelete = (
        <div>
            {`${t('validationMessage.modalDelete')}`}
        </div>
    );

    const modalFooterDelete = (
        <>
            <Button className="box-green border-0" onClick={handleModalDelete}>
                {t('button.delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const modalContentReactivate = (
        <div>
            {`${t('validationMessage.modalReactivate')}`}
        </div>
    );

    const modalFooterReactivate = (
        <>
            <Button className="box-green border-0" onClick={handleModalReactivate}>
                {t('button.reactivate')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayReactivateModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const columns = [
        "value",
        "name",
        "relatedOrgs",
    ];

    const columnsToExport = ["value", "name", "relatedOrgs"];

    const listOptions = {
        "model": CostBearerModel,
        "columns": columns,
        "actions": {
            "actionColumn": renderActionColumn,
        },
        "exportIsAvailable": true,
        "urlGetListItemsToExport": "/costbearer/list-all",
        "nameFile": t('costBearer.costBearer'),
        "ignoreFields": columnsToExport,
        "modelType": "CostBearer",
        "filterTestIsAvailable": true,
        "urlGetSearch": "/costbearer/list-all",
        "viewName": "AdminCostBearer",
        "modernDesign": true,
        // KBAR column only as wide as needed, more room for the related organizations
        "columnWeights": { "value": 0.3, "relatedOrgs": 1.6 },
        "mobileCard": {
            "title": (item) => item.name,
            "subtitle": (item) => (item.relatedOrgs || []).join(", "),
            "value": (item) => item.value,
            "valueCaption": CostBearerModel.getAttributes().value.label,
        },
    };

    return (
        <Col lg="12" className='mb-5 p-0 position-relative'>
            <CustomModal 
                isOpen={displayDeleteModal} 
                toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                body={modalContentDelete} 
                footer={modalFooterDelete}
            />

            <CustomModal 
                isOpen={displayReactivateModal} 
                toggle={() => setDisplayReactivateModal(!displayReactivateModal)}
                body={modalContentReactivate} 
                footer={modalFooterReactivate}
            />

            <SettingsListHeader
                createText={t('costBearer.newCostBearer')}
                showCreate={permissionUpdate}
                onCreate={() => setCreatePanelOpen(true)}
            />

            <CostBearerCreatePanel
                isOpen={createPanelOpen}
                onClose={() => setCreatePanelOpen(false)}
                onCreated={() => setTimeout(() => CustomReload(ADMIN_PATHS.ADMIN_COST_BEARER), 1500)}
            />

            <CostBearerUpdatePanel
                isOpen={editCostBearerId !== null}
                costBearerId={editCostBearerId}
                onClose={() => setEditCostBearerId(null)}
                onUpdated={() => setTimeout(() => CustomReload(ADMIN_PATHS.ADMIN_COST_BEARER), 1500)}
            />

            <SortableList listOptions={listOptions} />
        </Col>
    );
};

export default AdminCostBearerList;